import React, { useState, useEffect } from 'react';
import { 
  People, 
  PersonAdd, 
  PersonRemove, 
  TrendingUp, 
  TrendingDown, 
  Search, 
  FilterList, 
  MoreVert, 
  Email, 
  Phone, 
  Business, 
  Star,
  AttachMoney,
  Schedule,
  LocationOn,
  CalendarToday,
  Visibility,
  Edit,
  Delete
} from '@mui/icons-material';

const SubscribersDashboard = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    // Mock data for RM360 subscribers
    const mockSubscribers = [
      {
        id: 1,
        name: 'Khaled Al-Maani',
        email: 'khaled.almaani@email.com',
        phone: '+962 79 123 4567',
        segment: 'Premium',
        status: 'Active',
        subscriptionDate: '2023-01-15',
        lastActivity: '2 hours ago',
        products: 4,
        totalValue: 45000,
        location: 'Amman, Jordan',
        source: 'Referral',
        engagement: 'High',
        riskProfile: 'Conservative',
        nextReview: '2024-02-15'
      },
      {
        id: 2,
        name: 'Nader Al-Masri',
        email: 'nader.almasri@email.com',
        phone: '+962 79 234 5678',
        segment: 'Priority',
        status: 'Active',
        subscriptionDate: '2023-03-22',
        lastActivity: '4 hours ago',
        products: 5,
        totalValue: 120000,
        location: 'Zarqa, Jordan',
        source: 'Direct',
        engagement: 'High',
        riskProfile: 'Moderate',
        nextReview: '2024-03-22'
      },
      {
        id: 3,
        name: 'Mahmoud Al-Jundi',
        email: 'mahmoud.aljundi@email.com',
        phone: '+1 555 123 4567',
        segment: 'Premium',
        status: 'Active',
        subscriptionDate: '2023-02-10',
        lastActivity: '6 hours ago',
        products: 3,
        totalValue: 85000,
        location: 'New York, USA',
        source: 'Online',
        engagement: 'Medium',
        riskProfile: 'Aggressive',
        nextReview: '2024-02-10'
      },
      {
        id: 4,
        name: 'Amira Al-Kurdi',
        email: 'amira.alkurdi@email.com',
        phone: '+33 1 23 45 67 89',
        segment: 'General Mass Market',
        status: 'Active',
        subscriptionDate: '2023-04-05',
        lastActivity: '1 day ago',
        products: 4,
        totalValue: 65000,
        location: 'Paris, France',
        source: 'Branch',
        engagement: 'Medium',
        riskProfile: 'Conservative',
        nextReview: '2024-04-05'
      },
      {
        id: 5,
        name: 'Yousef Al-Zoubi',
        email: 'yousef.alzoubi@email.com',
        phone: '+1 555 987 6543',
        segment: 'Premium',
        status: 'Active',
        subscriptionDate: '2023-01-30',
        lastActivity: '1 day ago',
        products: 2,
        totalValue: 95000,
        location: 'Toronto, Canada',
        source: 'Referral',
        engagement: 'High',
        riskProfile: 'Moderate',
        nextReview: '2024-01-30'
      },
      {
        id: 6,
        name: 'Saja Al-Dabbas',
        email: 'saja.aldabbas@email.com',
        phone: '+962 79 345 6789',
        segment: 'Priority',
        status: 'Active',
        subscriptionDate: '2023-05-12',
        lastActivity: '2 days ago',
        products: 3,
        totalValue: 75000,
        location: 'Irbid, Jordan',
        source: 'Direct',
        engagement: 'Medium',
        riskProfile: 'Conservative',
        nextReview: '2024-05-12'
      },
      {
        id: 7,
        name: 'Mona Ibrahim',
        email: 'mona.ibrahim@email.com',
        phone: '+962 79 456 7890',
        segment: 'Employee Banking',
        status: 'Active',
        subscriptionDate: '2023-06-18',
        lastActivity: '2 days ago',
        products: 2,
        totalValue: 45000,
        location: 'Aqaba, Jordan',
        source: 'Employee',
        engagement: 'Low',
        riskProfile: 'Conservative',
        nextReview: '2024-06-18'
      },
      {
        id: 8,
        name: 'Leen Abu Zaid',
        email: 'leen.abuzaid@email.com',
        phone: '+962 79 567 8901',
        segment: 'Priority',
        status: 'Active',
        subscriptionDate: '2023-07-25',
        lastActivity: '3 days ago',
        products: 4,
        totalValue: 68000,
        location: 'Madaba, Jordan',
        source: 'Online',
        engagement: 'High',
        riskProfile: 'Moderate',
        nextReview: '2024-07-25'
      }
    ];

    setSubscribers(mockSubscribers);
  }, []);

  const handleSubscriberSelect = (subscriberId) => {
    setSelectedSubscribers(prev => 
      prev.includes(subscriberId) 
        ? prev.filter(id => id !== subscriberId)
        : [...prev, subscriberId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSubscribers.length === subscribers.length) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(subscribers.map(subscriber => subscriber.id));
    }
  };

  const getSegmentColor = (segment) => {
    switch (segment) {
      case 'Premium':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Priority':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Employee Banking':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      default:
        return 'bg-[#808080] text-[#FFFFFF]';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Inactive':
        return 'bg-[#808080] text-[#FFFFFF]';
      case 'Pending':
        return 'bg-[#FFA726] text-[#0A0A0A]';
      default:
        return 'bg-[#2A2A2A] text-[#B0B0B0]';
    }
  };

  const getEngagementColor = (engagement) => {
    switch (engagement) {
      case 'High':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Medium':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Low':
        return 'bg-[#808080] text-[#FFFFFF]';
      default:
        return 'bg-[#2A2A2A] text-[#B0B0B0]';
    }
  };

  const getRiskProfileColor = (riskProfile) => {
    switch (riskProfile) {
      case 'Conservative':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Moderate':
        return 'bg-[#B4E50D] text-[#0A0A0A]';
      case 'Aggressive':
        return 'bg-[#FF6B6B] text-[#FFFFFF]';
      default:
        return 'bg-[#2A2A2A] text-[#B0B0B0]';
    }
  };

  const filteredSubscribers = subscribers.filter(subscriber => {
    const matchesSearch = subscriber.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         subscriber.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         subscriber.segment.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || subscriber.segment === filter;
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredSubscribers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSubscribers = filteredSubscribers.slice(startIndex, endIndex);

  const totalSubscribers = subscribers.length;
  const activeSubscribers = subscribers.filter(s => s.status === 'Active').length;
  const totalValue = subscribers.reduce((sum, s) => sum + s.totalValue, 0);
  const averageEngagement = subscribers.filter(s => s.engagement === 'High').length;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-[#FFFFFF]">Subscribers</h1>
          <p className="text-[#B0B0B0] mt-1">Manage your customer subscription base and engagement</p>
        </div>
        <div className="flex items-center space-x-6">
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Total Subscribers</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{totalSubscribers}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Active</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{activeSubscribers}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Total Value</div>
            <div className="text-2xl font-bold text-[#B4E50D]">${(totalValue / 1000).toFixed(0)}K</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">High Engagement</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{averageEngagement}</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#2A2A2A]">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-[#B0B0B0]" />
            <input
              type="text"
              placeholder="Search subscribers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] placeholder-[#B0B0B0] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
            >
              <option value="all">All Segments</option>
              <option value="Premium">Premium</option>
              <option value="Priority">Priority</option>
              <option value="Employee Banking">Employee Banking</option>
              <option value="General Mass Market">General Mass Market</option>
            </select>
            
            <button className="p-3 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-xl text-[#B0B0B0] hover:text-[#B4E50D] transition-all duration-200">
              <FilterList />
            </button>
          </div>
        </div>
      </div>

      {/* Subscribers List */}
      <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] overflow-hidden">
        {/* Subscribers List Header */}
        <div className="bg-[#2A2A2A] px-6 py-4 border-b border-[#3A3A3A]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectedSubscribers.length === subscribers.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-[#B4E50D] bg-[#2A2A2A] border-[#3A3A3A] rounded focus:ring-[#B4E50D] focus:ring-2"
              />
              <span className="text-sm text-[#B0B0B0]">
                {selectedSubscribers.length > 0 ? `${selectedSubscribers.length} selected` : 'Select all'}
              </span>
            </div>
            
            {selectedSubscribers.length > 0 && (
              <div className="flex items-center space-x-2">
                <button className="p-2 bg-[#B4E50D] hover:bg-[#B4E50D] rounded-lg text-[#0A0A0A] transition-colors">
                  <Email />
                </button>
                <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                  <Edit />
                </button>
                <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#B0B0B0] hover:text-[#FF6B6B] transition-colors">
                  <Delete />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Subscriber Items */}
        <div className="divide-y divide-[#2A2A2A]">
          {currentSubscribers.map((subscriber) => (
            <div 
              key={subscriber.id}
              className="px-6 py-4 hover:bg-[#2A2A2A] transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-start space-x-4">
                <input
                  type="checkbox"
                  checked={selectedSubscribers.includes(subscriber.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSubscriberSelect(subscriber.id);
                  }}
                  className="w-4 h-4 text-[#B4E50D] bg-[#2A2A2A] border-[#3A3A3A] rounded focus:ring-[#B4E50D] focus:ring-2 mt-1"
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSegmentColor(subscriber.segment)}`}>
                          {subscriber.segment}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(subscriber.status)}`}>
                          {subscriber.status}
                        </span>
                      </div>
                      <span className="text-sm text-[#B4E50D] font-medium">
                        ${subscriber.totalValue.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-[#B0B0B0]">
                      <span>Last: {subscriber.lastActivity}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-medium text-[#FFFFFF] text-lg">
                          {subscriber.name}
                        </span>
                        <span className="text-sm text-[#B0B0B0]">({subscriber.products} products)</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 text-sm">
                            <Email className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">{subscriber.email}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <Phone className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">{subscriber.phone}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <LocationOn className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">{subscriber.location}</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 text-sm">
                            <Star className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">Engagement:</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEngagementColor(subscriber.engagement)}`}>
                              {subscriber.engagement}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <AttachMoney className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">Risk Profile:</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskProfileColor(subscriber.riskProfile)}`}>
                              {subscriber.riskProfile}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <CalendarToday className="text-[#B4E50D] w-4 h-4" />
                            <span className="text-[#B0B0B0]">Next Review: {subscriber.nextReview}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-4">
                      <button className="p-2 hover:bg-[#2A2A2A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <Visibility />
                      </button>
                      <button className="p-2 hover:bg-[#2A2A2A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <Edit />
                      </button>
                      <button className="p-2 hover:bg-[#2A2A2A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <MoreVert />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-[#2A2A2A] border-t border-[#3A3A3A]">
            <div className="flex items-center justify-between">
              <div className="text-sm text-[#B0B0B0]">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredSubscribers.length)} of {filteredSubscribers.length} subscribers
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribersDashboard;
